import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AamBackendApi } from 'src/libs/aamBackendApi';
import { validate } from 'uuid';

import { PageWrapper } from '@components/app/PageWrapper/PageWrapper';
import { useInjection } from '@context/inversify-context-provider';
import { TYPES } from '@libs/InversifyType';
import { Onboarding } from '@models/onboarding.model';
import { CircularProgress } from '@mui/material';

import { CandidatesRetriggeredTable } from './components/CandidatesRetriggeredTable';

export const BulkRetriggerPage: FC = () => {
  const { t } = useTranslation(['recruiter']);
  const aamBackendApi = useInjection<AamBackendApi>(TYPES.AamBackendApi);
  const [onboardingsRetriggered, setOnboardingsRetriggered] = useState<Onboarding[]>([]);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const invalidUUIDs = (uniqueCandidatesList: string[]): string[] => {
    const invalidUUID: string[] = [];

    for (const UUID of uniqueCandidatesList) {
      const isValid = validate(UUID);
      if (!isValid) invalidUUID.push(UUID);
    }

    return invalidUUID;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const textarea = e.currentTarget.elements.namedItem('userIds') as HTMLTextAreaElement | null;
    const input = textarea?.value.trim();

    if (!input) return;

    const inputCleaned = input
      .split(/\r?\n/)
      .map((id) => id.trim())
      .filter(Boolean);

    const uniqueIds = Array.from(new Set(inputCleaned));

    const invalidatedUUIDs = invalidUUIDs(uniqueIds);
    if (invalidatedUUIDs.length) {
      alert(`No valid UUIDs found: ${JSON.stringify(invalidatedUUIDs)}`);
      return;
    }

    try {
      setIsFetching(true);
      const result = await aamBackendApi.retriggerCandidates(uniqueIds);

      setIsFetching(false);
      setOnboardingsRetriggered(result);
    } catch (err) {
      alert(err?.message ?? err);
      setIsFetching(false);
    }
  };

  return (
    <div className="admin">
      <PageWrapper
        pageName="bulk-retrigger"
        featureName="bulk-retrigger"
        sideMenu={true}
        headerConfig={{ pageName: t('recruiter:GENERAL.BULKRETRIGGER.title'), languagePicker: true }}
      >
        <div className="tag-ds">
          <div className="feature-bulk-retrigger">
            <form onSubmit={handleSubmit}>
              <label className="tag-ds  large">{t('recruiter:GENERAL.CANDIDATES_OVERVIEW.filterDetails')}:</label>
              <textarea name="userIds" placeholder="Please insert your userId list here" />
              <div className="button-list">
                <button type="reset" disabled={isFetching}>
                  Reset
                </button>
                <button type="submit" disabled={isFetching}>
                  Retrigger
                </button>
              </div>
            </form>
            {isFetching && (
              <div className="loader">
                <CircularProgress />
              </div>
            )}
          </div>
          {onboardingsRetriggered.length > 0 && (
            <CandidatesRetriggeredTable onboardingsRetriggered={onboardingsRetriggered} />
          )}
        </div>
      </PageWrapper>
    </div>
  );
};
